import { gql } from "@apollo/client/core";
import { graphQlManager } from "./server";

/** For fetching metafields on many entities at once */
const metafieldsQuery = gql`
    query GetManyMetafields($entityIds: [String!]!) {
        metafieldsMany(entityIds: $entityIds) {
            id
            value
            metafieldConfiguration {
                id
                name
            }
        }
    }
`;

// This is a helper class that can be used to load metafields for a given entity.
// It will load the metafields for the entity and store them in a map.
// TODO: There are a number of areas in the code that request metafields in manual ways. Update each to use this service.
class MetafieldManager {
    readonly internalMap = new Map<string, { key: string; value: string }[]>();

    /**
     * Given a specific entity, request the given keys for that entity.
     * This will first check if the keys are already present in the internal map, and if not, it will fetch them from the server.
     * @param entityId The entity to check.
     * @param keys The keys to look for.
     * @returns Returns map of keys and values for the given entity. Missing keys will not be present. Use map.has() to check if a key was present.
     */
    async requestKeysForEntity(entityId: string, keys: string[]): Promise<Map<string, string>> {
        const loadedKeys = new Map<string, string>();
        await this.ensureIdsArePresent([entityId]);
        keys.forEach((key) => {
            const value = this.requestKeyForEntityIfAvailable(entityId, key);
            if (value) {
                loadedKeys.set(key, value);
            }
        });
        return loadedKeys;
    }

    async ensureIdsArePresent(entityIds: string[]) {
        // If ids are already present we bust out, we already have what we need
        if (entityIds.every((id) => this.internalMap.has(id))) return;
        // Request the metafields for required entities.
        const queryResponse = await graphQlManager.getShadowGraphqlClient().query<{
            metafieldsMany: { id: string; value: string; metafieldConfiguration: { id: string; name: string } }[][];
        }>({
            query: metafieldsQuery,
            errorPolicy: "all",
            variables: {
                entityIds: entityIds.filter((id) => !this.internalMap.has(id)),
            },
        });
        // Map the data to a better representation for retrieval.
        entityIds.forEach((id, idx) => {
            const entityFields = queryResponse.data.metafieldsMany[idx];
            this.internalMap.set(
                id,
                entityFields.map((lf) => {
                    return { key: lf.metafieldConfiguration.name, value: lf.value };
                }),
            );
        });
    }

    /**
     * Returns a metafield for this product if available. Fetching from the server if not available locally.
     * @param entityId The entity to request a field for.
     * @param key The field name to request
     * @returns A promise resolving to the field if available or undefined if not.
     */
    async requestKeyForEntity(entityId: string, key: string): Promise<string | undefined> {
        if (!this.internalMap.has(entityId)) {
            return undefined;
        }
        let entityFields = this.internalMap.get(entityId)!;
        const field = entityFields.find((ef) => ef.key === key);
        if (!field) {
            await this.ensureIdsArePresent([entityId]);
            entityFields = this.internalMap.get(entityId)!;
        }
        return field ? JSON.parse(field.value) : undefined;
    }

    /**
     * Returns a metafield for this product if available. Checks locally only, use ensureIdsArePresent to fetch first from the server.
     * @param entityId The entity to request a field for.
     * @param key The field name to request
     * @returns A promise resolving to the field if available or undefined if not.
     */
    requestKeyForEntityIfAvailable(entityId: string, key: string): string | undefined {
        if (!this.internalMap.has(entityId)) {
            return undefined;
        }
        const entityFields = this.internalMap.get(entityId)!;
        const field = entityFields.find((ef) => ef.key === key);
        if (!field) {
            return undefined;
        }
        return JSON.parse(field.value);
    }
}

const metafieldManager = new MetafieldManager();

export { metafieldManager };
